import clsx from "clsx";
import React from "react";
import DatePicker, { type DatePickerProps } from "react-datepicker";
import { useField } from "react-final-form";
import { makeStyles } from "tss-react/mui";

import { Box, FormHelperText, Theme } from "@mui/material";

const useStyles = makeStyles()((theme: Theme) => ({
    dateInputWrapper: {
        position: "relative",
        width: "100%",
    },
    calendarIcon: {
        position: "absolute",
        right: theme.spacing(3),
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
    },
    dateInput: {
        width: "100%",
        height: 40,
        padding: theme.spacing(1, 10, 1, 1.5),
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.spacing(1),
        fontSize: "1rem",
        boxSizing: "border-box",
        cursor: "pointer",
        "&:hover": {
            borderColor: theme.palette.grey[400],
        },
        "&:focus": {
            outline: "none",
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
        },
    },
}));

type CustomDateInputProps = {
    value?: string;
    onClick?: React.MouseEventHandler<HTMLInputElement>;
    placeholder?: string;
    id?: string;
};
const CustomDateInput = React.forwardRef<HTMLInputElement, CustomDateInputProps>((props, ref) => {
    const { classes } = useStyles();
    return (
        <Box className={classes.dateInputWrapper}>
            <input
                ref={ref}
                id={props.id}
                className={classes.dateInput}
                value={props.value}
                onClick={props.onClick}
                onChange={() => {}}
                placeholder={props.placeholder}
                readOnly
            />
            <i className={clsx(classes.calendarIcon, "fa-regular fa-calendar")} />
        </Box>
    );
});
CustomDateInput.displayName = "CustomDateInput";

export function DateField({ placeholder, ...props }: DatePickerProps & { placeholder?: string }) {
    const { classes } = useStyles();
    return (
        <DatePicker
            portalId="date-picker-portal"
            placeholderText={placeholder}
            dateFormat="dd/MM/yyyy"
            showYearDropdown
            showMonthDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={10}
            wrapperClassName={classes.dateInputWrapper}
            customInput={<CustomDateInput />}
            {...props}
        />
    );
}

export function ControlledDateField({
    name,
    onChange: _onChange,
    onCalendarClose: _onCalendarClose,
    ...props
}: DatePickerProps & { name: string; placeholder?: string }) {
    const { input, meta } = useField<Date>(name, { type: "date" });
    const error = meta.touched && meta.error;
    const selected = input.value || null;

    return (
        <Box>
            <DateField id={name} selected={selected} onChange={input.onChange} onCalendarClose={input.onBlur} {...props} />
            {error ? <FormHelperText error>{error}</FormHelperText> : null}
        </Box>
    );
}
